import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Bell } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { fetchNotifications, fetchUnreadCount, markAsRead, markAllAsRead } from '../store/notificationsSlice';
import { filterNotificationsForClient } from '../utils/notificationSecurity';
import NotificationItem from './NotificationItem';

export default function NotificationBell() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { notifications, unreadCount, loading } = useSelector((state) => state.notifications);
  const [isOpen, setIsOpen] = useState(false);

  // CRITICAL SECURITY: Filter notifications for client users
  const filteredNotifications = useMemo(() => 
    filterNotificationsForClient(notifications, user),
    [notifications, user]
  );

  // Filter to show only last 15 days notifications in the bell
  const recentNotifications = useMemo(() => {
    const fifteenDaysAgo = new Date();
    fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
    
    return filteredNotifications.filter(n => 
      new Date(n.created_at) >= fifteenDaysAgo
    );
  }, [filteredNotifications]);

  // Calculate unread count from filtered notifications (for client security)
  const displayUnreadCount = useMemo(() => {
    const fifteenDaysAgo = new Date();
    fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
    
    return filteredNotifications.filter(n => 
      !n.is_read && new Date(n.created_at) >= fifteenDaysAgo
    ).length;
  }, [filteredNotifications]);

  useEffect(() => {
    // Fetch initial notifications (fetch more for better filtering)
    dispatch(fetchNotifications({ limit: 100 }));
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  useEffect(() => {
    // Periodically fetch notifications
    const interval = setInterval(() => {
      dispatch(fetchNotifications({ limit: 100 }));
      dispatch(fetchUnreadCount());
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, [dispatch]);

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      dispatch(markAsRead(notification.id));
    }
    
    // Navigate based on action_url or entity type
    if (notification.action_url) {
      window.location.href = notification.action_url;
    } else if (notification.ticket_id) {
      // Legacy: ticket-specific navigation
      window.location.href = `/tickets/${notification.ticket_id}`;
    } else if (notification.entity_type && notification.entity_id) {
      // Generic: navigate based on entity type
      window.location.href = `/${notification.entity_type}s/${notification.entity_id}`;
    }
    
    setIsOpen(false);
  };

  const handleMarkSingleAsRead = (notificationId) => {
    dispatch(markAsRead(notificationId));
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllAsRead());
  };

  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (open) {
      // Refresh notifications when opening
      dispatch(fetchNotifications({ limit: 100 }));
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {displayUnreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
            >
              {displayUnreadCount > 99 ? '99+' : displayUnreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0 bg-white dark:bg-slate-900 dark:border-slate-700 border-slate-200" align="end">
        <div className="flex items-center justify-between p-4 border-b dark:border-slate-700 border-slate-200">
          <h3 className="font-semibold text-lg text-slate-900 dark:text-white">Notifications</h3>
          {displayUnreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleMarkAllAsRead}
              className="text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
            >
              Mark all as read
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-[400px]">
          {loading && recentNotifications.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400">
              Loading notifications...
            </div>
          ) : recentNotifications.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400">
              <Bell className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No recent notifications</p>
              <p className="text-xs mt-1">Last 15 days</p>
            </div>
          ) : (
            <div className="divide-y dark:divide-slate-700 divide-slate-200">
              {recentNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={() => handleNotificationClick(notification)}
                  onMarkAsRead={handleMarkSingleAsRead}
                />
              ))}
            </div>
          )}
        </ScrollArea>
        
        <Separator className="dark:bg-slate-700" />
        <div className="p-2">
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            onClick={() => {
              navigate('/notifications');
              setIsOpen(false);
            }}
          >
            View all notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
